/** Client Wall types (mirrors API). */
export type WallBlockType =
  | "text"
  | "header"
  | "divider"
  | "image"
  | "quote"
  | "news"
  | "button"
  | "globalCounter"
  | "poll"
  | "progress"
  | "stat"
  | "playerSpotlight"
  | "featuredPreset"
  | "featuredSpeedrun"
  | "randomFact"
  | "countdown"
  | "link";

export type WallBlock = {
  id: string;
  type: WallBlockType | "stack";
  data: Record<string, unknown>;
};

export type WallLiveData = {
  counters: Record<string, number>;
  progress: Record<string, { current: number; goal: number }>;
  polls: Record<
    string,
    { options: string[]; counts: number[]; percents: number[]; myVote: number | null }
  >;
  stats: {
    totalGames: number;
    totalDeaths: number;
    totalPoints: number;
    totalUsers: number;
    totalPlaytimeSec: number;
  } | null;
  spotlight: {
    id: string;
    displayName: string;
    bio: string | null;
    totalDeaths: number;
    totalPlayTimeSec: number;
  } | null;
  featuredPresets: Array<{
    id: string;
    name: string;
    userId: string;
    displayName: string | null;
    updatedAt: string;
  }>;
  featuredSpeedruns: Array<{
    id: string;
    displayName: string | null;
    level: number;
    timeMs: number;
    at: string;
  }>;
  randomFacts: Record<string, string>;
};

export const WALL_BLOCK_LABELS: Record<WallBlockType, string> = {
  text: "Text",
  header: "Header",
  divider: "Divider",
  image: "Image",
  quote: "Quote",
  news: "News",
  button: "Button",
  globalCounter: "Global Counter Button",
  poll: "Poll",
  progress: "Progress",
  stat: "Stat",
  playerSpotlight: "Player Spotlight",
  featuredPreset: "Featured Preset",
  featuredSpeedrun: "Featured Speedrun",
  randomFact: "Random Fact",
  countdown: "Countdown",
  link: "Link",
};

export function wallBlockLabel(type: string): string {
  if (type === "stack") return "Card Stack";
  return WALL_BLOCK_LABELS[type as WallBlockType] ?? type;
}

export const WALL_BLOCK_TYPES = Object.keys(WALL_BLOCK_LABELS) as WallBlockType[];

export function newBlockId(): string {
  return `blk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

export function defaultBlockData(type: WallBlockType): Record<string, unknown> {
  switch (type) {
    case "text":
      return { title: "", body: "Enter text…" };
    case "header":
      return { title: "Section Title", subtitle: "" };
    case "divider":
      return {};
    case "image":
      return { imageId: "", width: 100, caption: "" };
    case "quote":
      return { text: "Quote text…" };
    case "news":
      return { title: "News", limit: 8 };
    case "button":
      return { label: "Button", confirm: false, confirmText: "Are you sure?", action: "none", linkSection: "home" };
    case "globalCounter":
      return { label: "", buttonText: "Press", cooldownSec: 0, showCount: true };
    case "poll":
      return { question: "Your question?", options: ["Option A", "Option B"] };
    case "progress":
      return { title: "Progress", goal: 10000000, counterBlockId: "" };
    case "stat":
      return { title: "Statistics", showGames: true, showDeaths: true, showPoints: true, showUsers: true, showPlaytime: false };
    case "playerSpotlight":
      return { title: "Player Spotlight" };
    case "featuredPreset":
      return { title: "Featured Presets", limit: 6 };
    case "featuredSpeedrun":
      return { title: "SpeedRun Records", limit: 8 };
    case "randomFact":
      return { title: "Did You Know?", refreshSec: 30, facts: [] };
    case "countdown":
      return { title: "Event Countdown", targetIso: "", label: "" };
    case "link":
      return { label: "Go to Rankings", section: "rankings" };
    default:
      return {};
  }
}

export function createBlock(type: WallBlockType): WallBlock {
  return { id: newBlockId(), type, data: defaultBlockData(type) };
}
